
import { DataStore } from "@aws-amplify/datastore";
import { Submissions } from "../../models/index.js";
import { PermissionType } from '../../Enums.js';
import { handleError } from "../../helpers/LogHelpers.js";
import { getLinkedSubmissionId } from "../../utils/AiReviewHelpers.js";

const parseJsonIfNeeded = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const getReviewReviewerId = (review) => {
  if (review?.userId != null) return String(review.userId);

  const parsedOwner = parseJsonIfNeeded(review?.meta?.owner);
  if (parsedOwner?.userId != null) return String(parsedOwner.userId);

  return "";
};

const getAcceptedSubmissionMatcher = (options = {}) => {
  if (typeof options.acceptedSubmissionMatcher === "function") {
    return options.acceptedSubmissionMatcher;
  }

  const acceptedFieldPath =
    typeof options.acceptedSubmissionFieldPath === "string" && options.acceptedSubmissionFieldPath.trim().length > 0
      ? options.acceptedSubmissionFieldPath
      : "data.status";

  const acceptedValue =
    options.acceptedSubmissionValue !== undefined ? options.acceptedSubmissionValue : "Accepted";

  return (submission) => {
    const segments = acceptedFieldPath.split(".");
    let currentValue = submission;

    for (const segment of segments) {
      if (currentValue == null) return false;

      currentValue =
        typeof currentValue === "string" ? parseJsonIfNeeded(currentValue)?.[segment] : currentValue?.[segment];
    }

    return currentValue === acceptedValue;
  };
};

export const fetchReviewingViewData = (submissionsData, reviewsData, activeReviewToken) => {
  console.log("Starting processing submissions for stats");
  console.log("submissionsData: ", submissionsData);
  console.log("reviewsData: ", reviewsData);
  console.log("activeReviewToken: ", activeReviewToken);

    let mysubmissions = submissionsData.filter(
      (item) =>
        item.meta.trackingNumber &&
        Number.isInteger(item.meta.trackingNumber)
    );

    //console.log("mysubmissions: ", mysubmissions);

    const myReviews = reviewsData.filter((item) => item.formToken === activeReviewToken);
    //console.log("myReviews: ", myReviews);

    for (let i = 0; i < mysubmissions.length; i++) {
      const submissionCopy = { ...mysubmissions[i] };
      const submissionId = mysubmissions[i].id;
      const rev = myReviews.filter(
        (item) => getLinkedSubmissionId(item) === submissionId
      );
      const nassignments = rev.length;
      const revcomp = rev.filter((item) => item.meta.complete);
      let total = 0;
      let N = 0;
      let average = 0;
      let stdev = 0;
      for (let j = 0; j < revcomp.length; j++) {
        if (typeof revcomp[j].data?.total === "number") {
          total += revcomp[j].data.total;
          N = N + 1;
        }
      }
      if (N != 0) {
        average = total / N;
      }
      stdev = 0;
      let variance = 0;
      let count = 0; // Counter for valid data points
      
      for (let j = 0; j < revcomp.length; j++) {
        if (typeof revcomp[j].data?.total === "number") {
          const deviation = revcomp[j].data.total - average;
          variance += deviation * deviation;
          count++;
        }
      }
      
      if (count > 1) {
        variance = variance / count;
        stdev = Math.sqrt(variance); // Standard deviation is the square root of the variance
      } else {
        stdev = 0; // Set to 0 if there's insufficient data
      }
      //console.log("submissionCopy: ", submissionCopy);
      //console.log(": ", );
      submissionCopy.total = total;
      submissionCopy.average = average;
      submissionCopy.stdev = stdev;
      submissionCopy.nbOfAssignements = nassignments;
      submissionCopy.nbOfCompletedAssignements = revcomp.length;
      mysubmissions[i] = submissionCopy;
      //console.log("mysubmissions[i]: ", mysubmissions[i]);
      //console.log("mysubmissions: ", mysubmissions);
    }
    return mysubmissions;
  };

export const fetchUsersViewData = (
  usersData = [],
  submissionsData = [],
  reviewsData = [],
  options = {},
  notifications = []
) => {
  if (!Array.isArray(usersData) || usersData.length === 0) {
    return [];
  }

  const safeSubmissions = Array.isArray(submissionsData) ? submissionsData : [];
  const safeReviews = Array.isArray(reviewsData) ? reviewsData : [];
  const safeNotifications = Array.isArray(notifications) ? notifications : [];
  const isAcceptedSubmission = getAcceptedSubmissionMatcher(options);

  return usersData.map((user) => {
    const userId = String(user?.id ?? "");

    const userSubmissions = safeSubmissions.filter(
      (submission) => String(submission?.userId ?? "") === userId
    );

    const userReviews = safeReviews.filter(
      (review) => getReviewReviewerId(review) === userId
    );

    const completedSubmissions = userSubmissions.filter(
      (submission) => submission?.meta?.complete === true
    );

    const acceptedSubmissions = userSubmissions.filter((submission) =>
      isAcceptedSubmission(submission)
    );

    const completedAssignments = userReviews.filter(
      (review) => review?.meta?.complete === true
    );

    const scoredAssignments = completedAssignments.filter(
      (review) => typeof review?.data?.total === "number"
    );

    const totalAssignmentScore = scoredAssignments.reduce(
      (sum, review) => sum + review.data.total,
      0
    );

    const averageScorePerAssignment =
      scoredAssignments.length > 0 ? totalAssignmentScore / scoredAssignments.length : 0;

    const relatedUpdatedDates = [...userSubmissions, ...userReviews]
      .map((item) => item?.updatedAt ?? item?.createdAt ?? null)
      .filter(Boolean)
      .map((value) => new Date(value).getTime())
      .filter((value) => Number.isFinite(value));

    const latestRelatedTimestamp =
      relatedUpdatedDates.length > 0 ? Math.max(...relatedUpdatedDates) : null;

    // Transient (~3s) flash array, drives bell + row/cell flash
    const userNotifications = safeNotifications.filter(
      (notification) => notification?.entityId === userId
    );

    const logins = Array.isArray(user?.logins) ? user.logins : [];
    const loginTimestamps = logins
      .map((value) => new Date(value).getTime())
      .filter((value) => Number.isFinite(value));
    const lastLogin = loginTimestamps.length > 0
      ? new Date(Math.max(...loginTimestamps)).toISOString()
      : "";

    return {
      ...user,
      numberOfSubmissions: userSubmissions.length,
      numberOfCompletedSubmissions: completedSubmissions.length,
      numberOfAcceptedSubmissions: acceptedSubmissions.length,
      numberOfAssignments: userReviews.length,
      numberOfCompletedAssignments: completedAssignments.length,
      averageScorePerAssignment,
      lastLogin,
      lastChangedDate: latestRelatedTimestamp ? new Date(latestRelatedTimestamp).toISOString() : "",
      notifications: userNotifications,
    };
  });
};
  
  export const fetchFilteredData = (
    submissionsData = [], // default to an empty array
    formToken = null, 
    isHidden = false, 
    isComplete = false,
    isNumbered = false
  ) => {
    if (!Array.isArray(submissionsData) || submissionsData.length === 0) {
      return []; // Return early if submissionsData is not an array or empty
    }
  
    return submissionsData.filter(item => {
      const matchesFormToken = formToken ? item.formToken === formToken : true;
      const matchesHidden = isHidden ? !item.hidden : true;
      const matchesComplete = isComplete ? item.meta.complete : true;
      const matchesNumbered = isNumbered ? Number.isInteger(item.meta.trackingNumber) : true;
      
      return matchesFormToken && matchesHidden && matchesComplete && matchesNumbered;
    });
  };


  export const compileReports = (existingReports, user, selectedFormName) => {
    try {
      // Input validation
      try {
        if (!existingReports || !Array.isArray(existingReports) || existingReports.length === 0 || !user) {
          const errMsg = "Invalid or empty existingReports or user data.";
          throw new Error(errMsg); // Throw an error for invalid input
        }
      } catch (error) {
        // Re-throw the error to the outer try-catch
        throw new Error(`Input validation failed: ${error.message}`);
      }
  
      // Function to check if the user has permission to read the report
      const canReadReport = (report) => {
        try {
          switch (report.readPermissions) {
            case PermissionType.SUPER_ADMIN:
              if (user.adminLevel !== PermissionType.SUPER_ADMIN) {
                throw new Error("User does not have SUPER_ADMIN permissions for this report.");
              }
              return true;
  
            case PermissionType.ADMIN:
              if (user.adminLevel !== PermissionType.ADMIN && user.adminLevel !== PermissionType.SUPER_ADMIN) {
                throw new Error("User does not have ADMIN permissions for this report.");
              }
              return true;
  
            case PermissionType.REVIEWERS:
              if (user.adminLevel !== PermissionType.ADMIN && user.adminLevel !== PermissionType.SUPER_ADMIN) {
                throw new Error("User does not have REVIEWER permissions for this report.");
              }
              return true;
  
            case PermissionType.INDIVIDUAL_USER:
              const individualUsers = Array.isArray(report.individualUsers)
                ? report.individualUsers
                : report.individualUsers.split(',');  // Handle array or comma-delimited strings
              if (!individualUsers.includes(user.userId)) {
                throw new Error("User does not have individual user permissions for this report.");
              }
              return true;
  
            default:
              throw new Error("No valid permissions found for report.");
          }
        } catch (error) {
          // Re-throw the error to the outer try-catch
          throw new Error(`Error in canReadReport: ${error.message}`);
        }
      };
  
      // Filter the existing reports based on the permissions logic
      const filteredExistingReports = existingReports.filter(canReadReport);
  
      // Return the filtered reports
      return [...filteredExistingReports];
  
    } catch (error) {
      // Outer catch block that handles all errors from inner try-catch blocks
      const errMsg = `Error in compileReports function in the form ${selectedFormName}: ${error.message}<p>Existing Reports: ${JSON.stringify(existingReports, null, 2)}</p><p>existingReports.length: ${existingReports ? existingReports.length : "Null"}</p>`;
      console.error(errMsg, { existingReports, user }); // Log the error in the console
      handleError(errMsg, user);  // Report the error using handleError
      return [];  // Return an empty array as a fallback value
    }
  };
  

 
  
  
  
  
